import { ConfigurationItemArrayEditor } from '@client/components/_core/configuration/ConfigurationItemArrayEditor';
import { ConfigurationItemEditor } from '@client/components/_core/configuration/ConfigurationItemEditor';
import { ConfigurationItemListEditor } from '@client/components/_core/configuration/ConfigurationItemListEditor';
import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { ClaimStatusTag } from '@client/components/claims/ClaimStatusTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { CLAIM_STATUS_TYPE_LABELS } from '@shared/constants/business/claim-status';
import { toYesNo } from '@shared/utils';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Form, Input, InputNumber, Space, Tag } from 'antd';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claims/general')({
    staticData: {
        metadata: {
            title: 'General',
        },
        ui: {
            menuDescription: 'Configure claims settings and workflows',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <General />
        </PageController>
    );
}

type QueryParams = RouterInputs['claimStatus']['listClaimStatuses'];

const columnHelper = createColumnHelper<
    RouterOutputs['claimStatus']['listClaimStatuses']['rows'][number],
    QueryParams['orderBy']
>();

function ClaimStatusesList() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.claimStatus.deleteClaimStatus.useMutation();
    const navigate = useNavigate();

    const [input, setInput] = useState<QueryParams>({});

    const { data, isPending, error } = trpc.claimStatus.listClaimStatuses.useQuery(input);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        pagination: {
            position: 'none',
        },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
                cell({ row }) {
                    return (
                        <ClaimStatusTag
                            variant="outlined"
                            data={{
                                name: row.name,
                                displayColor: row.displayColor,
                            }}
                        />
                    );
                },
            }),
            columnHelper.accessor('type', {
                id: 'type',
                header: 'Type',
                sortable: 'type',
                cell({ row }) {
                    return (
                        <Tag variant="outlined" color={row.type === 'OPEN' ? 'green' : 'blue'}>
                            {CLAIM_STATUS_TYPE_LABELS[row.type]}
                        </Tag>
                    );
                },
            }),
            columnHelper.accessor('isDefault', {
                id: 'isDefault',
                header: 'Is default',
                sortable: 'isDefault',
                cell({ row }) {
                    return (
                        <Tag variant="outlined" color={row.isDefault ? 'success' : 'default'}>
                            {toYesNo(row.isDefault)}
                        </Tag>
                    );
                },
            }),
            columnHelper.actions({
                cell({ row }) {
                    return (
                        <Button.Delete
                            onConfirm={(onSuccess) =>
                                mutate(
                                    { id: row.id },
                                    {
                                        onSuccess() {
                                            onSuccess();
                                            void trpcUtils.claimStatus.invalidate();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    );
                },
            }),
        ],
    });

    return (
        <TRPCErrorBoundary error={error}>
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/claims/general/claim-statuses/edit/$claimStatusId',
                                params: {
                                    orgId,
                                    claimStatusId: id,
                                },
                            },
                            e,
                        );
                    },
                })}
            >
                <PrismaTable.Header
                    title="Statuses"
                    actions={
                        <Button.Add.Link
                            to="/console/$orgId/configuration/claims-products/claims/general/claim-statuses/add"
                            params={{
                                orgId,
                            }}
                            variant="tertiary"
                            label="New status"
                        />
                    }
                />
            </PrismaTable>
        </TRPCErrorBoundary>
    );
}

function General() {
    return (
        <>
            <Outlet />
            <Space orientation="vertical" size="middle" className="w-full">
                <ConfigurationItemArrayEditor
                    title="Claim decisions"
                    settingToEdit="CLAIM_DECISIONS"
                    description="Rename, add or remove a claim decision."
                    itemRules={[{ required: true, message: 'Field is required' }]}
                    className="w-124"
                />
                <ClaimStatusesList />
                <ConfigurationItemListEditor
                    title="Claim timeline items"
                    settingToEdit="CLAIM_TIMELINE_ITEMS"
                    description="Rename, add or remove a claim timeline item."
                >
                    <Form.Item name="internalName" label="Internal name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="label" label="Label" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="labelWhenCompleted" label="Label when completed" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="order" label="Order" rules={[{ required: true }]}>
                        <InputNumber />
                    </Form.Item>
                    <Form.Item name="prepopulate" label="Prepopulate" valuePropName="checked">
                        <Input type="checkbox" />
                    </Form.Item>
                    <Form.Item name="createAsCompleted" label="Create as completed" valuePropName="checked">
                        <Input type="checkbox" />
                    </Form.Item>
                    <Form.Item name="hideFromUi" label="Hide from UI" valuePropName="checked">
                        <Input type="checkbox" />
                    </Form.Item>
                </ConfigurationItemListEditor>
                <ConfigurationItemEditor
                    title="Triage outcome"
                    settingToEdit="CLAIM_TRIAGE_OUTCOME"
                    description="Rename, add or remove a triage outcome item."
                    editor={{
                        height: '300px',
                    }}
                />
            </Space>
        </>
    );
}
